import { useState, useEffect, useRef } from "react";
import { BannerSlot } from "@/components/BannerSlot";
import { usePageMeta } from "@/lib/seo";
import { Link, useSearchParams } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import api from "@/lib/api";
import { PromoCodeField } from "@/components/PromoCodeField";

const discounted = (price, promo) => {
  if (!promo) return price;
  const off = promo.discount_type === "percent" ? (price * promo.discount_value) / 100 : Math.min(promo.discount_value, price);
  return Math.max(0, Math.round((price - off) * 100) / 100);
};

export default function PricingPage() {
  usePageMeta("Pricing", "Simple monthly plans for auto repair shops. Compare The ShopAdvisor™ plans and add-ons.");
  const [plans, setPlans] = useState([]);
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [polling, setPolling] = useState(false);
  const [promo, setPromo] = useState(null);
  const pollStarted = useRef(false);

  useEffect(() => {
    api.get("/api/pricing-plans").then(r => setPlans(r.data)).catch(() => {});
  }, []);

  // Poll for Stripe checkout status (once per return)
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId || pollStarted.current) return;
    pollStarted.current = true;
    setPolling(true);
    let attempts = 0;
    const finish = () => { setPolling(false); setSearchParams({}, { replace: true }); pollStarted.current = false; };
    const poll = async () => {
      if (attempts >= 8) { toast.error("We couldn't confirm your payment yet. Refresh in a minute if you were charged."); setPolling(false); return; }
      attempts++;
      try {
        const { data } = await api.get(`/api/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") { toast.success("Payment successful! Your subscription is now active."); finish(); return; }
        if (data.status === "expired") { toast.error("Payment session expired. Please try again."); finish(); return; }
      } catch { /* retry */ }
      setTimeout(poll, 2000);
    };
    poll();
  }, [searchParams, setSearchParams]);

  const handleSubscribe = async (planId) => {
    if (!user) {
      toast.info("Please login to subscribe.");
      return;
    }
    try {
      const originUrl = window.location.origin;
      const { data } = await api.post("/api/checkout/session", { plan_id: planId, origin_url: originUrl, promo_code: promo?.code || "" });
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create checkout session");
    }
  };

  const tierColors = ["border-slate-200", "border-blue-400 shadow-lg", "border-slate-200"];

  return (
    <div className="min-h-screen bg-white pt-24 pb-20 px-6 md:px-12 lg:px-24" data-testid="pricing-page">
      <BannerSlot placement="pricing" className="max-w-6xl mx-auto mb-12 rounded-2xl overflow-hidden" />
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-blue-600 mb-3">Pricing</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-slate-900 mb-4">Simple, transparent pricing</h1>
          <p className="text-lg text-slate-500 max-w-xl mx-auto">Choose the plan that fits your business. Scale up anytime.</p>
        </div>
        <div className="max-w-sm mx-auto mb-10" data-testid="pricing-promo">
          <PromoCodeField amount={100} onChange={setPromo} testIdPrefix="pricing-promo" />
        </div>
        {polling && <div className="text-center mb-6 text-blue-600 font-medium animate-pulse">Verifying payment...</div>}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan, i) => (
            <div key={plan.id} className={`bg-white border ${tierColors[i] || "border-slate-200"} rounded-xl p-8 relative ${plan.is_popular ? "ring-2 ring-blue-500" : ""}`} data-testid={`pricing-card-${plan.name.toLowerCase()}`}>
              {plan.is_popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-bold px-4 py-1 rounded-full">
                  Most Popular
                </div>
              )}
              <h3 className="text-2xl font-bold text-slate-900 mb-2">{plan.name}</h3>
              <div className="flex items-baseline gap-1 mb-6">
                {promo && <span className="text-xl text-slate-400 line-through mr-1" data-testid={`pricing-original-${plan.name.toLowerCase()}`}>${plan.price}</span>}
                <span className="text-4xl font-black text-slate-900" data-testid={`pricing-price-${plan.name.toLowerCase()}`}>${discounted(plan.price, promo).toFixed(2).replace(/\.00$/, "")}</span>
                <span className="text-slate-500 text-sm">/{plan.interval}</span>
              </div>
              <div className="space-y-3 mb-8">
                {(plan.features || []).map((f, j) => (
                  <div key={j} className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                    <span className="text-slate-600 text-sm">{f}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => handleSubscribe(plan.id)}
                className={`w-full inline-flex items-center justify-center gap-2 py-3 rounded-lg font-medium text-sm transition-colors ${plan.is_popular ? "bg-blue-600 hover:bg-blue-500 text-white font-bold" : "bg-slate-900 hover:bg-slate-800 text-white"}`}
                data-testid={`pricing-select-${plan.name.toLowerCase()}`}
              >
                Get Started <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        {plans.length === 0 && (
          <div className="text-center py-12 text-slate-400">Loading plans...</div>
        )}
      </div>
    </div>
  );
}
